import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AutoDiscoveryBanner from '@/components/InsuranceSection/AutoDiscoveryBanner';
import { VERIFICATION_FIELDS } from '@/components/InsuranceSection/constants';
import { InsuranceFormValues } from '@/components/InsuranceSection/types';
import { getSelfPayInsuranceFields } from '@/components/InsuranceSection/utils/getSelfPayInsuranceFields';
import { getSortedInsurancePolicyAndVerificationValues } from '@/components/InsuranceSection/utils/getSortedInsurancePolicyAndVerificationValues';
import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { RecordType } from '@/components/PersonInPlaceEditor/components/InPlaceEditor/types';
import AppContext from '@/contexts/appContext';
import useInsurancePolicyFormFields from '@/hooks/useInsurancePolicyFormFields';
import PersonContext from '@/pages/Person/PersonContext';
import InsurancePolicy from '@/pages/Profile/components/InsurancePolicy';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import { insuranceSectionSchema } from '@/pages/Profile/schemas';
import InsuranceRepository from '@/repositories/InsuranceRepository';
import InsuranceVerificationRepository from '@/repositories/InsuranceVerificationRepository';
import { InsurancePolicy as InsurancePolicyInterface } from '@/types/insurancePolicy';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const InsuranceSection = () => {
  const insuranceRepository = InsuranceRepository();
  const insuranceVerificationRepository = InsuranceVerificationRepository();
  const { addMessage } = useContext(AppContext);
  const {
    person: {
      id: personId,
      insurance_policies,
      has_run_discovery,
      most_recent_insurance_discovery
    },
    refetch: refetchPerson
  } = useContext(PersonContext);

  const { buildFields, submittedValuesManipulator } = useInsurancePolicyFormFields({
    includeVerificationFields: true
  });

  const insurancePolicyMutationPromise = async ({
    id,
    values
  }: {
    id?: string;
    values: Partial<InsurancePolicyInterface>;
  }) => {
    if (!Object.keys(values).length) {
      return;
    }

    if (!id) {
      return await insuranceRepository.createInsurancePolicy(values);
    }

    return await insuranceRepository.updateInsurancePolicy(id, values);
  };

  const mutationFn = async ({
    id,
    values
  }: {
    id?: string;
    values: Partial<InsuranceFormValues>;
  }) => {
    if (values.new_verification_toggle) {
      const sortedFields = getSortedInsurancePolicyAndVerificationValues(values);

      if (id) {
        await insurancePolicyMutationPromise({
          id,
          values: sortedFields.insurancePolicyFields
        });
      } else {
        const insurancePolicyResponse = await insurancePolicyMutationPromise({
          id,
          values: sortedFields.insurancePolicyFields
        });
        if (!insurancePolicyResponse) return;
        id = insurancePolicyResponse.id;
      }

      return insuranceVerificationRepository.createInsuranceVerification({
        ...sortedFields.insuranceVerificationFields,
        insurance_policy_id: id
      });
    }

    return await insurancePolicyMutationPromise({ id, values });
  };

  const mutateInsurancePolicy = useMutation({
    mutationFn,
    onSuccess: async (response) => {
      if (!response) return;
      await refetchPerson();
      addMessage('success', 'Insurance Policy saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteInsurancePolicy = useMutation({
    mutationFn: (id: string) => insuranceRepository.deleteInsurancePolicy(id),
    onSuccess: async () => {
      await refetchPerson();
      addMessage('success', 'Insurance Policy deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  // Calculate the count of discovered policies from the external verification
  const discoveredPoliciesCount =
    most_recent_insurance_discovery?.discovered_policy_verifications?.length ?? 0;

  return (
    <div className="relative mb-4">
      <SectionHeading text="Insurance" />
      {has_run_discovery && most_recent_insurance_discovery && (
        <div className="mb-4">
          <AutoDiscoveryBanner
            mostRecentDiscovery={most_recent_insurance_discovery}
            policiesCount={discoveredPoliciesCount}
          />
        </div>
      )}
      {insurance_policies.map((insurancePolicy) => {
        const policyFields = buildFields({
          personIdField: false,
          insuranceCardImages: insurancePolicy.insurance_card_images
        });

        return (
          <div key={insurancePolicy.id} className="group/subsection mb-4 relative">
            <InPlaceEditor
              deleteMutation={deleteInsurancePolicy}
              disableEdit={mutateInsurancePolicy.isPending || deleteInsurancePolicy.isPending}
              editButtonClass="top-6"
              fields={
                insurancePolicy.is_self_pay ? getSelfPayInsuranceFields(policyFields) : policyFields
              }
              key={insurancePolicy.id}
              mutation={mutateInsurancePolicy}
              record={
                {
                  ...insurancePolicy,
                  co_insurance: insurancePolicy.co_insurance
                    ? insurancePolicy.co_insurance * 100
                    : null,
                  co_pay: insurancePolicy.co_pay ? insurancePolicy.co_pay / 100 : null,
                  out_of_pocket_maximum: insurancePolicy.out_of_pocket_maximum
                    ? insurancePolicy.out_of_pocket_maximum / 100
                    : null,
                  new_verification_toggle: false,
                  [VERIFICATION_FIELDS.status]: null
                } as InsurancePolicyInterface
              }
              recordType={RecordType.InsurancePolicy}
              schema={insuranceSectionSchema}
              submittedValuesManipulator={submittedValuesManipulator}
              subsectionEditor>
              <InsurancePolicy
                insurance_policy={insurancePolicy}
                mostRecentDiscovery={most_recent_insurance_discovery}
              />
            </InPlaceEditor>
          </div>
        );
      })}

      <div className="group/subsection relative mt-3">
        <InPlaceEditor
          schema={insuranceSectionSchema}
          subsectionEditor
          fields={buildFields({ personIdField: true })}
          mutation={mutateInsurancePolicy}
          newRecord
          record={
            {
              id: undefined,
              carrier: null,
              new_verification_toggle: false,
              person_id: personId
            } as InsurancePolicyInterface
          }
          recordType={RecordType.InsurancePolicy}
          submittedValuesManipulator={submittedValuesManipulator}
        />
      </div>
    </div>
  );
};

export default InsuranceSection;
